import { InfoCircleOutlined } from "@ant-design/icons";
import { UserNotificationChannel } from "@axl/membership-sdk";
import { Checkbox, notification, Table, Tooltip } from "antd";
import { useCallback, useState } from "react";

import { useLoc, useSdk } from "@app/shared/lib/hooks";

type Props = {
  readonlyEmail: boolean;
  readonlyTg: boolean;
  channels: UserNotificationChannel[];
  updateChannelSettings: (updatedChannel: UserNotificationChannel) => void;
};

export const NotificationChannelTable: React.FC<Props> = ({
  readonlyEmail,
  readonlyTg,
  channels,
  updateChannelSettings,
}) => {
  const sdk = useSdk();
  const loc = useLoc();
  const [loading, setLoading] = useState(false);

  const createOrSave = useCallback(
    async (
      channel: UserNotificationChannel,
      changes: Partial<UserNotificationChannel>,
    ) => {
      setLoading(true);
      let success = false;
      if (channel.id == null) {
        const result = await sdk.api.notification.channel.create({
          notificationType: channel.notificationType,
          useSite: changes.useSite ?? channel.useSite,
          useEmail: changes.useEmail ?? channel.useEmail,
          useTg: changes.useTg ?? channel.useTg,
        });

        if (result.success)
          channel.update({
            ...changes,
            id: result.body.id,
          });
        updateChannelSettings(channel);
        success = result.success;
      } else {
        const result = await sdk.api.notification.channel.save(
          channel.id,
          changes,
        );
        if (result.success) {
          channel.update(changes);
          updateChannelSettings(channel);
        }

        success = result.success;
      }

      setLoading(false);
      if (!success)
        notification.error({ message: loc.word("Error occured"), duration: 2 });
    },
    [loc, sdk.api.notification.channel, updateChannelSettings],
  );

  return (
    <Table<UserNotificationChannel>
      loading={loading}
      rowKey={(x) => x.notificationType}
      dataSource={channels}
      pagination={false}
      scroll={{ x: true }}
      size="small"
      columns={[
        {
          title: loc.word("Notif.typeLabel", { default: "Type" }),
          render: (_text, item) => {
            return (
              <div className="flex align-center gap-5">
                {loc.word(`Notif.type.${item.notificationType}.title`)}
                {loc.hasWord(`Notif.type.${item.notificationType}.help`) && (
                  <Tooltip
                    title={loc.word(`Notif.type.${item.notificationType}.help`)}
                  >
                    <InfoCircleOutlined />
                  </Tooltip>
                )}
              </div>
            );
          },
        },
        {
          align: "center",
          title: loc.word("Notif.emailLabel", { default: "Email" }),
          render: (_text, item) => {
            return (
              <Checkbox
                checked={item.useEmail}
                disabled={readonlyEmail}
                onChange={(v) =>
                  createOrSave(item, { useEmail: v.target.checked })
                }
              >
                {readonlyEmail && (
                  <Tooltip
                    title={loc.word("Notif.useEmailDisabledLabel", {
                      default:
                        "You need to enable receiving notification via email",
                    })}
                  >
                    <InfoCircleOutlined />
                  </Tooltip>
                )}
              </Checkbox>
            );
          },
        },
        {
          align: "center",
          title: loc.word("Notif.tgLabel", { default: "Telegram" }),
          render: (_text, item) => {
            return (
              <Checkbox
                checked={item.useTg}
                disabled={readonlyTg}
                onChange={(v) =>
                  createOrSave(item, { useTg: v.target.checked })
                }
              >
                {readonlyTg && (
                  <Tooltip
                    title={loc.word("Notif.useTgDisabledLabel", {
                      default:
                        "You need to enable receiving notification via Telegram",
                    })}
                  >
                    <InfoCircleOutlined />
                  </Tooltip>
                )}
              </Checkbox>
            );
          },
        },
      ]}
    />
  );
};
